import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Loader2, Plus, Search } from 'lucide-react';
import { vehicleApi } from '../services/vehicleApi';
import { dealApi } from '../services/dealApi';
import { cashflowApi } from '../services/cashflowApi';
import {
  KIND_LABEL, KIND_STYLE, Participant, STATUS_LABEL, Vehicle, VehicleKind, VehicleStatus, shortWon,
} from '../types/vehicle';
import { Deal, Valuation } from '../types/deal';
import { Cashflow } from '../types/cashflow';
import { computeMetrics, mult, pct } from '../lib/metrics';
import { VehicleFormModal } from '../components/VehicleFormModal';
import { SidePanel } from '../components/SidePanel';
import { VehicleDetail } from './VehicleDetailPage';
import { Tour, TourButton, TourStep } from '../components/Tour';

/**
 * 자금원 목록 — 워크플로우 1단계 · factsheet 「재원 대시보드」 대조 (2026-10-08).
 *
 * **통장 하나 = 한 행**이다. 위에 자금 구분별 합계(약정 · 납입 · 투자 · 회수 · IRR · MOIC),
 * 아래에 목록(표 하나). 고유자금과 위탁자금의 합계는 따로 낸다 — 합치지 않는다.
 * 카드 보기는 2026-11-02 뺐다 — 자금원이 몇 개뿐이라 둘 다 둘 까닭이 없다(factsheet 는 39개라 둘 다 둔다).
 */

export const VehicleListPage: React.FC = () => {
  const [items, setItems] = useState<Vehicle[]>([]);
  const [parts, setParts] = useState<Participant[]>([]);
  const [deals, setDeals] = useState<Deal[]>([]);
  const [flows, setFlows] = useState<Cashflow[]>([]);
  const [values, setValues] = useState<Record<number, Valuation>>({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<VehicleStatus | 'all'>('all');
  const [tourRun, setTourRun] = useState(false);
  // 줄을 누르면 오른쪽 패널로 상세를 연다(UI 규칙 — 화면을 옮기지 않는다)
  const [sel, setSel] = useState<Vehicle | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, p, d, f, lv] = await Promise.all([
        vehicleApi.list(), vehicleApi.allParticipants(), dealApi.list(), cashflowApi.list(), dealApi.latestValues(),
      ]);
      setItems(v); setParts(p); setDeals(d); setFlows(f); setValues(lv);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const stat = useCallback((ids: Set<number>) => {
    const open = deals.filter((d) => ids.has(d.vehicleId) && d.stage !== '회수' && d.stage !== '상각');
    const res = open.reduce((s, d) => s + (values[d.id]?.ourValue ?? 0), 0);
    const m = computeMetrics(flows.filter((f) => ids.has(f.vehicleId)), res);
    // 평가액 없는 보유 건이 있으면 MOIC · IRR 을 「—」로 — 0 으로 세면 0.00x(전액 손실)처럼 보인다
    if (open.some((d) => !values[d.id])) { m.tvpi = null; m.irr = null; }
    const committed = parts.filter((p) => ids.has(p.vehicleId)).reduce((s, p) => s + (p.committedAmount ?? 0), 0);
    return { m, committed };
  }, [deals, values, flows, parts]);

  const shown = items.filter((v) => (status === 'all' || v.status === status) && (!q.trim() || v.name.includes(q.trim())));
  const unchecked = items.filter((v) => v.kind === 'entrusted' && !v.legalForm);

  const TOUR: TourStep[] = useMemo(() => [
    { name: 'summary', target: '[data-tour="summary"]', title: '자금 구분별 합계', content: '위탁자금과 고유자금을 따로 합칩니다. 약정 · 납입은 참여자와 원장에서, IRR · MOIC 는 원장과 평가액에서 계산합니다.' },
    { name: 'tools', target: '[data-tour="tools"]', title: '찾기', content: '이름으로 찾고, 운용 중 · 종료로 거릅니다.' },
    { name: 'add', target: '[data-tour="add"]', title: '자금원 추가', content: '자금 구분을 먼저 고릅니다. 법적 형태는 몰라도 비워 두고 저장할 수 있습니다.', placement: 'left' },
    { name: 'list', target: '[data-tour="list"]', title: '통장 하나 = 한 줄', content: '자금원마다 약정 · 납입 · 투자 · 회수와 IRR · MOIC. 줄을 누르면 오른쪽에 상세가 열립니다.', placement: 'top' },
  ], []);

  if (loading) return <div className="flex items-center justify-center gap-2 py-24 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> 불러오는 중...</div>;

  const chip = (on: boolean) => `px-2.5 py-1 rounded-lg text-xs font-medium border ${on ? 'bg-slate-900 border-slate-900 text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`;

  return (
    <div className="p-6 lg:p-8 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">자금원 <TourButton onClick={() => setTourRun(true)} /></h1>
        <button data-tour="add" onClick={() => setOpen(true)}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 flex-shrink-0">
          <Plus size={13} /> 자금원 추가
        </button>
      </div>

      <div data-tour="summary" className="space-y-2 mb-4">
        {(['entrusted', 'own'] as VehicleKind[]).map((k) => {
          const ids = new Set(items.filter((v) => v.kind === k).map((v) => v.id));
          if (ids.size === 0) return null;
          const { m, committed } = stat(ids);
          return (
            <div key={k} className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 items-stretch">
              <div className="flex items-center"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${KIND_STYLE[k]}`}>{KIND_LABEL[k]} {ids.size}</span></div>
              {[
                ['약정', committed ? shortWon(committed) : '—'], ['납입', shortWon(m.contributed)], ['투자', shortWon(m.invested)],
                ['회수', shortWon(m.returned)], ['IRR', pct(m.irr)], ['MOIC', mult(m.tvpi)],
              ].map(([l, v]) => (
                <div key={l} className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                  <div className="text-xs text-slate-400">{l}</div>
                  <div className="text-base font-bold text-slate-900 tabular-nums">{v}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {unchecked.length > 0 && (
        <div data-tour="unchecked" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-amber-900">
            <AlertTriangle size={14} /> 법적 형태 확인 전 {unchecked.length}건
            <span className="font-normal text-amber-800">— {unchecked.map((v) => v.name).join(' · ')}</span>
          </h2>
        </div>
      )}

      <div data-tour="tools" className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <label className="relative">
            <span className="sr-only">자금원 이름 찾기</span>
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input id="vehicle-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="자금원 이름"
              className="pl-7 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white w-48" />
          </label>
          {(['all', 'active', 'closed'] as const).map((s) => (
            <button key={s} className={chip(status === s)} onClick={() => setStatus(s)}>{s === 'all' ? '전체 상태' : STATUS_LABEL[s]}</button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 py-16 text-center text-sm text-slate-500">조건에 맞는 자금원이 없습니다</div>
      ) : (
        <div data-tour="list" className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-400">
                <th className="text-left font-medium px-4 py-2.5">자금원명</th><th className="text-left font-medium px-2 py-2.5">형태</th>
                <th className="text-right font-medium px-2 py-2.5">약정</th><th className="text-right font-medium px-2 py-2.5">납입</th>
                <th className="text-right font-medium px-2 py-2.5">투자</th><th className="text-right font-medium px-2 py-2.5">회수</th>
                <th className="text-right font-medium px-2 py-2.5">IRR</th><th className="text-right font-medium px-2 py-2.5">MOIC</th>
                <th className="text-left font-medium px-4 py-2.5">상태</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((v) => {
                const { m, committed } = stat(new Set([v.id]));
                return (
                  <tr key={v.id} onClick={() => setSel(v)}
                    className={`border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50 ${sel?.id === v.id ? 'bg-indigo-50/60' : ''}`}>
                    <td className="px-4 py-3">
                      <button type="button" onClick={(e) => { e.stopPropagation(); setSel(v); }} className="font-medium text-slate-900 hover:text-indigo-600 text-left">{v.name}</button>
                    </td>
                    <td className="px-2 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${KIND_STYLE[v.kind]}`}>{v.legalForm ?? KIND_LABEL[v.kind]}</span></td>
                    <td className="px-2 py-3 text-right tabular-nums">{committed ? shortWon(committed) : '—'}</td>
                    <td className="px-2 py-3 text-right tabular-nums">{shortWon(m.contributed)}</td>
                    <td className="px-2 py-3 text-right tabular-nums">{shortWon(m.invested)}</td>
                    <td className="px-2 py-3 text-right tabular-nums">{shortWon(m.returned)}</td>
                    <td className="px-2 py-3 text-right tabular-nums">{pct(m.irr)}</td>
                    <td className="px-2 py-3 text-right tabular-nums font-semibold">{mult(m.tvpi)}</td>
                    <td className="px-4 py-3"><span className="rounded-full px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600">{STATUS_LABEL[v.status]}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sel && (
        <SidePanel
          onClose={() => { setSel(null); load(); }}
          header={
            <div className="flex items-center gap-2 text-xs text-slate-500">
              자금원 상세
              <Link to={`/vehicles/${sel.id}`} className="inline-flex items-center gap-0.5 text-indigo-600 hover:underline">
                전체 화면 <ExternalLink size={11} />
              </Link>
            </div>
          }
        >
          <VehicleDetail key={sel.id} vehicleId={sel.id} inPanel />
        </SidePanel>
      )}

      <VehicleFormModal open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
      <Tour steps={TOUR} run={tourRun} onFinish={() => setTourRun(false)} />
    </div>
  );
};
